import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import usePageTitle from "../hooks/usePageTitle";
import { HiOutlineLightBulb } from "react-icons/hi2";
import {
  FaUserTie,
  FaUserSecret,
  FaUserShield,
  FaUserGraduate,
  FaRegBuilding,
  FaSitemap,
  FaHandshake,
  FaInfinity,
  FaExclamationCircle,
} from "react-icons/fa";

const ABSOLUTE_HOST = "https://dev.sliitleo.org";

function readUserFromStorage() {
  try {
    const raw = JSON.parse(localStorage.getItem("user")) || {};
    return { name: "Unknown User", role: "Member", image: "/user.png", ...raw };
  } catch {
    return { name: "Unknown User", role: "Member", image: "/user.png" };
  }
}

/* ------------------------------------------------------------------------
   Small presentational helpers -- no state, no side effects. They only
   render markup/styles built from the shared design tokens so the
   selectable-card look stays consistent across the three choice groups
   (host type, project type, officer type) instead of three near-duplicate
   blocks of ad hoc Tailwind color classes.
-------------------------------------------------------------------------*/

function SectionHead({ index, title, desc, id }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: "var(--sp-4)" }}>
      <div
        className="num"
        style={{
          display: "grid",
          placeItems: "center",
          width: 28,
          height: 28,
          borderRadius: 999,
          background: "var(--surface-2)",
          border: "1px solid var(--border)",
          color: "var(--accent)",
          fontWeight: 700,
          fontSize: 13,
          flexShrink: 0,
        }}
      >
        {index}
      </div>
      <div style={{ minWidth: 0 }}>
        <div id={id} style={{ fontWeight: 700, fontSize: 14.5, color: "var(--text-1)" }}>{title}</div>
        {desc && <div style={{ fontSize: 12.5, color: "var(--text-2)", marginTop: 2 }}>{desc}</div>}
      </div>
    </div>
  );
}

function SelectableCard({ icon, image, label, description, selected, onClick, stacked = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      style={{
        display: "flex",
        flexDirection: stacked ? "column" : "row",
        alignItems: "center",
        textAlign: stacked ? "center" : "left",
        gap: stacked ? 8 : 12,
        width: "100%",
        padding: stacked ? "16px 12px" : "14px 16px",
        borderRadius: "var(--radius-md)",
        border: `1px solid ${selected ? "var(--accent)" : "var(--border)"}`,
        background: selected ? "rgba(124,58,237,0.14)" : "var(--surface-2)",
        transition: "border-color .15s ease, background-color .15s ease",
      }}
    >
      <span
        style={{
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          width: stacked ? 36 : 40,
          height: stacked ? 36 : 40,
          borderRadius: "50%",
          background: selected ? "var(--accent)" : "var(--surface-3)",
          color: selected ? "#fff" : "var(--accent)",
        }}
      >
        {image ? (
          <img
            src={image}
            alt={label}
            style={{ width: stacked ? 22 : 24, height: stacked ? 22 : 24, borderRadius: "50%", objectFit: "cover" }}
          />
        ) : (
          icon
        )}
      </span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: "block", fontWeight: 700, fontSize: 13, color: "var(--text-1)" }}>{label}</span>
        {description && (
          <span style={{ display: "block", fontSize: 11.5, color: "var(--text-2)", marginTop: 2 }}>
            {description}
          </span>
        )}
      </span>
    </button>
  );
}

export default function CreateProjectPage() {
  const user = useMemo(() => readUserFromStorage(), []);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadCatError, setLoadCatError] = useState("");

  // form state
  const [projectName, setProjectName] = useState("");
  const [hostType, setHostType] = useState("");
  const [projectType, setProjectType] = useState("");
  const [officerType, setOfficerType] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [phaseCount, setPhaseCount] = useState("");

  // config
  const hostTypes = [
    { value: "club", label: "Club Project", description: "Host Club: Leo Club of SLIIT", icon: <FaRegBuilding className="text-xl" /> },
    { value: "district", label: "On behalf of a District Project", description: "", icon: <FaSitemap className="text-xl" />, image: "https://www.lionsclubs.org/resources/109141433" },
    { value: "multiple", label: "On behalf of Multiple District Project", description: "", icon: <FaSitemap className="text-xl" />, image: "https://www.lionsclubs.org/resources/109141433" },
  ];
  const projectTypes = [
    { value: "service", label: "Club Service Project", description: "Host Club: Leo Club of SLIIT", icon: <FaRegBuilding className="text-xl" /> },
    { value: "joint", label: "Joint Project with another Leo/Non-Leo Club(s)", description: "", icon: <FaHandshake className="text-xl" /> },
    { value: "continuous", label: "Continuous Project", description: "Phase options 1–10", icon: <FaInfinity className="text-xl" /> },
  ];
  const officerTypes = [
    { icon: <FaUserGraduate className="text-xl" />, value: "leader", label: "Project Leader" },
    { icon: <FaUserTie className="text-xl" />, value: "chairperson", label: "Project Chairperson" },
    { icon: <FaUserSecret className="text-xl" />, value: "secretary", label: "Project Secretary" },
    { icon: <FaUserShield className="text-xl" />, value: "treasurer", label: "Project Treasurer" },
  ];

  // Phase options logic:
  // - Normal: [1,2,3]
  // - Continuous: [1..10]
  const phaseOptions = useMemo(() => {
    if (projectType === "continuous") return Array.from({ length: 10 }, (_, i) => i + 1);
    return [1, 2, 3];
  }, [projectType]);

  // Keep phaseCount valid if projectType changes
  useEffect(() => {
    if (!phaseOptions.includes(Number(phaseCount))) {
      setPhaseCount(""); // reset if out-of-range
    }
  }, [projectType]); // eslint-disable-line react-hooks/exhaustive-deps

  // load categories
  useEffect(() => {
    (async () => {
      try {
        setLoadCatError("");
        const res = await fetch(`${ABSOLUTE_HOST}/Backend/get_categories.php`, {
          headers: { "Content-Type": "application/json" },
        });
        const data = await res.json();
        if (data?.success && Array.isArray(data.categories)) {
          setCategories(data.categories);
        } else {
          setCategories([]);
          setLoadCatError(data?.message || "Failed to load categories");
        }
      } catch {
        setLoadCatError("Failed to load categories");
      }
    })();
  }, []);

  const formValid =
    projectName.trim().length >= 3 &&
    hostType &&
    projectType &&
    officerType &&
    categoryId &&
    Number(phaseCount) >= 1;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formValid || submitting) return;

    const payload = {
      SLIITID: user?.SLIITID || null,
      projectName: projectName.trim(),
      hostType,
      projectType,
      officerType,
      categoryId: Number(categoryId),
      phaseCount: Number(phaseCount || 0),
    };

    try {
      setSubmitting(true);
      const res = await fetch(`${ABSOLUTE_HOST}/Backend/create_project.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data?.success) {
        setProjectName("");
        setHostType("");
        setProjectType("");
        setOfficerType("");
        setCategoryId("");
        setPhaseCount("");
        alert("Project created successfully!");
      } else {
        alert(data?.message || "Unable to create project.");
      }
    } catch (err) {
      alert("Network/Server error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex text-white min-h-screen" style={{ background: "var(--bg-base, #070a12)" }}>
      <Sidebar />
      <Topbar
        user={{
          name: user?.name || "Member",
          role: user?.role || "Member",
          image: user?.image || "/user.png",
        }}
      />

      <main className="lg:ml-64 mt-14 ml-0 w-full px-4 sm:px-6 md:px-8 py-6 md:py-10">
        <div className="mx-auto w-full" style={{ maxWidth: 880 }}>
          {/* Page head */}
          <div className="page-head">
            <div style={{ minWidth: 0 }}>
              <div className="eyebrow" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    display: "grid",
                    placeItems: "center",
                    width: 24,
                    height: 24,
                    borderRadius: 999,
                    background: "var(--accent)",
                    color: "#fff",
                    flexShrink: 0,
                  }}
                >
                  <HiOutlineLightBulb size={13} />
                </span>
                Club Project
              </div>
              <h1 style={{ marginTop: 6 }}>Create Project</h1>
              <div className="page-desc">
                Choose the project type, fill in the details, and assign the founding committee to submit it for review.
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* SECTION 1 — Project Type */}
            <section className="panel" style={{ padding: "var(--sp-5)", marginBottom: "var(--sp-5)" }}>
              <SectionHead
                index={1}
                title="Project Type"
                desc="Choose who is hosting the project and how it will be organized."
              />

              <div style={{ marginBottom: "var(--sp-5)" }}>
                <div className="field-label" id="host-type-label" style={{ marginBottom: 10 }}>
                  Project Host Type<span className="req">*</span>
                </div>
                <div role="group" aria-labelledby="host-type-label" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {hostTypes.map((item) => (
                    <SelectableCard
                      key={item.value}
                      icon={item.icon}
                      image={item.image}
                      label={item.label}
                      description={item.description}
                      selected={hostType === item.value}
                      onClick={() => setHostType(item.value)}
                    />
                  ))}
                </div>
              </div>

              <div>
                <div className="field-label" id="project-type-label" style={{ marginBottom: 10 }}>
                  Project Type (Organizing Type)<span className="req">*</span>
                </div>
                <div role="group" aria-labelledby="project-type-label" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {projectTypes.map((item) => (
                    <SelectableCard
                      key={item.value}
                      icon={item.icon}
                      label={item.label}
                      description={item.description}
                      selected={projectType === item.value}
                      onClick={() => setProjectType(item.value)}
                    />
                  ))}
                </div>
              </div>
            </section>

            {/* SECTION 2 — Project Details */}
            <section className="panel" style={{ padding: "var(--sp-5)", marginBottom: "var(--sp-5)" }}>
              <SectionHead
                index={2}
                title="Project Details"
                desc="Name your project and place it in the right category."
              />

              <div className="field">
                <label className="field-label" htmlFor="projectName">
                  Project Name<span className="req">*</span>
                </label>
                <input
                  id="projectName"
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="Ex: ගිට් කැම්ප් / GitCamp"
                />
                <div className="field-hint">Enter the name of your project (minimum 3 characters)</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="field" style={{ marginBottom: 0 }}>
                  <label className="field-label" htmlFor="categoryId">
                    Project Category<span className="req">*</span>
                  </label>
                  <select id="categoryId" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                    <option value="">Select a category</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {loadCatError ? (
                    <div className="field-error">
                      <FaExclamationCircle size={11} /> {loadCatError}
                    </div>
                  ) : (
                    <div className="field-hint">One category can contain many projects</div>
                  )}
                </div>

                <div className="field" style={{ marginBottom: 0 }}>
                  <label className="field-label" htmlFor="phaseCount">
                    Number of Phases<span className="req">*</span>
                  </label>
                  <select
                    id="phaseCount"
                    value={phaseCount}
                    onChange={(e) => setPhaseCount(Number(e.target.value))}
                  >
                    <option value="">Select phase count</option>
                    {phaseOptions.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                  <div className="field-hint">
                    {projectType === "continuous"
                      ? "Continuous projects: choose 1–10 phases."
                      : "Default projects: choose 1–3 phases."}
                  </div>
                </div>
              </div>
            </section>

            {/* SECTION 3 — Project Team */}
            <section className="panel" style={{ padding: "var(--sp-5)", marginBottom: "var(--sp-5)" }}>
              <SectionHead
                index={3}
                title="Project Team"
                desc="Select the officer role you'll hold for this project."
                id="officer-type-label"
              />

              <div role="group" aria-labelledby="officer-type-label" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                {officerTypes.map((item) => (
                  <SelectableCard
                    key={item.value}
                    icon={item.icon}
                    label={item.label}
                    stacked
                    selected={officerType === item.value}
                    onClick={() => setOfficerType(item.value)}
                  />
                ))}
              </div>

              {!officerType && (
                <div className="field-error" style={{ marginTop: 12 }}>
                  <FaExclamationCircle size={11} /> Please select an officer type
                </div>
              )}
            </section>

            {/* Submit */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!formValid || submitting}
                className="btn btn-primary w-full md:w-auto"
              >
                {submitting && <span className="spinner" />}
                {submitting ? "Creating…" : "Create Project"}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
